import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FiX, FiFile } from 'react-icons/fi';
import { API_URL } from '../config';

const lbl = { display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', margin: '12px 0 4px' };
const inp = { width: '100%', padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const fmtBytes = (b) => (b == null ? '' : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`);
// The recipient being typed (after the last comma), so several can be added.
const lastToken = (s) => ((s || '').split(',').pop() || '').trim();

/**
 * Reusable "Email to Client" modal — sends selected crew Pictures (kind 'photo')
 * and/or Documents (kind 'attachment') to the client with a Complete/Update message.
 *
 * Props:
 *   jobId
 *   items: [{ id, kind: 'photo'|'attachment', name, mime, thumbUrl, size, caption }]
 *   onClose: (sent: boolean) => void
 */
export default function EmailToClientModal({ jobId, items, onClose }) {
  const token = localStorage.getItem('authToken');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const [recipients, setRecipients] = useState('');
  const [tpl, setTpl] = useState('complete');
  const [templates, setTemplates] = useState({ complete: { subject: '', message: '' }, update: { subject: '', message: '' } });
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const key = (i) => `${i.id}:${i.kind}`;
  const [sel, setSel] = useState(() => new Set(items.map(key)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [toHits, setToHits] = useState([]);      // "To" autocomplete suggestions
  const toDebounce = useRef();

  // Look up only the address being typed — clients, job contacts, teammates and the
  // agency directory (so a building official can be added by name).
  const onRecipientsChange = (val) => {
    setRecipients(val);
    clearTimeout(toDebounce.current);
    const tok = lastToken(val);
    if (tok.length < 2) { setToHits([]); return; }
    toDebounce.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/email-contacts?q=${encodeURIComponent(tok)}`, config);
        setToHits(res.data || []);
      } catch (e) { setToHits([]); }
    }, 220);
  };

  const pickRecipient = (email) => {
    const prefix = (recipients || '').split(',').slice(0, -1).map(s => s.trim()).filter(Boolean);
    setRecipients([...prefix, email].join(', ') + ', ');
    setToHits([]);
  };

  useEffect(() => {
    axios.get(`${API_URL}/jobs/${jobId}/email-attachments/defaults`, config)
      .then((res) => {
        setRecipients((res.data?.recipients || []).join(', '));
        const t = res.data?.templates || {};
        setTemplates(t);
        const cur = t.complete || {};
        setSubject(cur.subject || '');
        setMessage(cur.message || '');
      })
      .catch(() => setErr('Could not load suggested recipients — you can still enter them below.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const switchTpl = (which) => {
    setTpl(which);
    const t = templates[which] || {};
    setSubject(t.subject || '');
    setMessage(t.message || '');
  };

  const toggle = (i) => setSel((prev) => {
    const n = new Set(prev);
    const k = key(i);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const selItems = items.filter((i) => sel.has(key(i)));
  const selBytes = selItems.reduce((s, i) => s + (i.size || 0), 0);

  const send = async () => {
    const recips = recipients.split(',').map((s) => s.trim()).filter(Boolean);
    if (!recips.length) { setErr('Add at least one recipient email.'); return; }
    if (!selItems.length) { setErr('Select at least one photo or document to send.'); return; }
    setBusy(true); setErr('');
    try {
      const photo_ids = selItems.filter((i) => i.kind === 'photo').map((i) => i.id);
      const attachment_ids = selItems.filter((i) => i.kind === 'attachment').map((i) => i.id);
      const res = await axios.post(`${API_URL}/jobs/${jobId}/email-attachments`,
        { recipients: recips, subject, message, photo_ids, attachment_ids }, config);
      const sent = res.data?.sent || 0;
      const over = res.data?.overflow || 0;
      alert(`Sent to ${sent} recipient${sent === 1 ? '' : 's'}.` + (over ? ` ${over} larger file(s) shared via the portal link.` : ''));
      onClose(true);
    } catch (e) {
      setErr('Send failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setBusy(false);
    }
  };

  const tabBtn = (which, label) => (
    <button onClick={() => switchTpl(which)} style={{ padding: '6px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid', borderColor: tpl === which ? '#2563eb' : '#cbd5e1', background: tpl === which ? '#2563eb' : '#fff', color: tpl === which ? '#fff' : '#334155' }}>{label}</button>
  );

  return (
    <div onClick={() => !busy && onClose(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>Email to client</h3>
          <button onClick={() => onClose(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><FiX /></button>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>{tabBtn('complete', 'Project complete')}{tabBtn('update', 'Progress update')}</div>
        <label style={lbl}>To <span style={{ fontWeight: 400, color: '#94a3b8' }}>(type a name to search — separate multiple with commas)</span></label>
        <div style={{ position: 'relative' }}>
          <input
            value={recipients}
            onChange={(e) => onRecipientsChange(e.target.value)}
            style={inp}
            placeholder="Start typing a name, or an email address"
            autoComplete="off"
          />
          {toHits.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 2100, background: 'white',
              border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 3,
              boxShadow: '0 6px 18px rgba(0,0,0,0.12)', maxHeight: 210, overflowY: 'auto',
            }}>
              {toHits.map((h, i) => (
                <button key={h.email + i} type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickRecipient(h.email)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '7px 10px', background: 'none', border: 'none', borderTop: i ? '1px solid #f1f5f9' : 'none', cursor: 'pointer', fontSize: 13 }}>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>{h.name}</span>
                    <span style={{ color: '#64748b' }}> · {h.email}</span>
                    {h.detail ? <span style={{ display: 'block', color: '#94a3b8', fontSize: 11.5 }}>{h.detail}</span> : null}
                  </span>
                  <span style={{
                    flexShrink: 0, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                    padding: '2px 6px', borderRadius: 6,
                    background: h.kind === 'Client' ? '#dcfce7' : h.kind === 'Contact' ? '#dbeafe' : h.kind === 'Team' ? '#f1f5f9' : '#fef3c7',
                    color: h.kind === 'Client' ? '#166534' : h.kind === 'Contact' ? '#1d4ed8' : h.kind === 'Team' ? '#475569' : '#92400e',
                  }}>{h.kind}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <label style={lbl}>Subject</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} style={inp} />
        <label style={lbl}>Message</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} style={{ ...inp, resize: 'vertical' }} />
        <label style={lbl}>Include <span style={{ fontWeight: 400, color: '#94a3b8' }}>({selItems.length} selected · {fmtBytes(selBytes)})</span></label>
        {items.length === 0 ? (
          <div style={{ fontSize: 13, color: '#94a3b8', padding: 8 }}>No photos or documents on this job yet.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(90px,1fr))', gap: 8, maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
            {items.map((i) => {
              const on = sel.has(key(i));
              const isImg = (i.mime || '').startsWith('image/');
              return (
                <div key={key(i)} onClick={() => toggle(i)} style={{ cursor: 'pointer', border: `2px solid ${on ? '#16a34a' : '#e5e7eb'}`, borderRadius: 8, padding: 4, position: 'relative', background: on ? '#f0fdf4' : '#fff' }}>
                  {isImg && i.thumbUrl ? (
                    <img src={i.thumbUrl} alt="" style={{ width: '100%', height: 64, objectFit: 'cover', borderRadius: 4 }} />
                  ) : (
                    <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}><FiFile size={26} /></div>
                  )}
                  <div style={{ fontSize: 10, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{i.name}</div>
                  {on && <div style={{ position: 'absolute', top: 4, right: 4, background: '#16a34a', color: '#fff', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>✓</div>}
                </div>
              );
            })}
          </div>
        )}
        {selBytes > 18 * 1024 * 1024 && <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>Large selection — the bigger files will be shared via a secure portal link instead of attached, so the email won't bounce.</div>}
        {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={() => onClose(false)} disabled={busy} style={{ flex: 1, padding: 12, background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={send} disabled={busy} style={{ flex: 2, padding: 12, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? 'Sending…' : 'Send Email'}</button>
        </div>
      </div>
    </div>
  );
}
